
import React, { useState } from 'react';
import { TemplateSet, BannerTemplate } from '@/types/bannerTemplate';
import { getTemplateSets, templateToDraft } from '@/utils/banner/templateUtils';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { BannerSize } from '@/types/banner';

interface TemplateCatalogProps {
  onSelectTemplate?: (templateId: string) => void;
}

const TemplateCatalog: React.FC<TemplateCatalogProps> = ({ onSelectTemplate }) => {
  const [templateSets] = useState<TemplateSet[]>(getTemplateSets());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Get unique categories from template sets
  const categories = Array.from(new Set(templateSets.map(set => set.category)));
  
  // Filter template sets by selected category
  const filteredTemplateSets = selectedCategory === 'all' 
    ? templateSets 
    : templateSets.filter(set => set.category === selectedCategory);
  
  const handleSelectTemplate = (template: BannerTemplate, size: BannerSize = 'medium_rectangle') => {
    // Save the template as a draft
    templateToDraft(template.id, size);
    
    toast({
      title: "Template Selected",
      description: `${template.name} template loaded for editing`,
    });
    
    // Navigate to the AI banner editor
    navigate('/master/marketing/ai-banner');
    
    // Call the callback if provided
    if (onSelectTemplate) {
      onSelectTemplate(template.id);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Banner Templates</h2>
        <Tabs defaultValue="all" value={selectedCategory} onValueChange={setSelectedCategory} className="w-auto">
          <TabsList>
            <TabsTrigger value="all">All Categories</TabsTrigger>
            {categories.map(category => (
              <TabsTrigger key={category} value={category}>{category}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      
      <div className="space-y-8">
        {filteredTemplateSets.map(templateSet => (
          <div key={templateSet.id} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">{templateSet.name}</h3>
              <Badge variant="outline">{templateSet.templates.length} templates</Badge>
            </div>
            <p className="text-sm text-gray-500">{templateSet.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templateSet.templates.map(template => (
                <Card key={template.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-[4/3] relative">
                    <img 
                      src={template.thumbnailUrl} 
                      alt={template.name} 
                      className="w-full h-full object-cover"
                    />
                    {template.formats.some(f => f.isAnimated) && (
                      <Badge className="absolute top-2 right-2 bg-blue-500">Animated</Badge>
                    )}
                  </div>
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <CardDescription>{template.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <div className="flex flex-wrap gap-1 mb-2">
                      {template.tags?.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500">
                      Available in {template.formats.length} sizes
                    </p>
                  </CardContent>
                  <CardFooter className="p-4 pt-0">
                    <Button 
                      className="w-full"
                      onClick={() => handleSelectTemplate(template)}
                    >
                      Use Template
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateCatalog;
